import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Ticket, Plus, Trash2, Power, Download, Search, X, Save, Layers } from "lucide-react";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusStyles = {
  free:     { bg: "#DCFCE7", fg: "#166534", label: "I lire" },
  reserved: { bg: "#FEF3C7", fg: "#92400E", label: "I rezervuar" },
  used:     { bg: "#E0E7FF", fg: "#3730A3", label: "I perdorur" },
  expired:  { bg: "#F1F5F9", fg: "#475569", label: "I skaduar" },
  disabled: { bg: "#FEE2E2", fg: "#991B1B", label: "I caktivizuar" },
};

const formatDate = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("sq-AL", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch { return iso; }
};

const AdminVouchers = () => {
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  const [showSingle, setShowSingle] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const [singleForm, setSingleForm] = useState({
    code: "", discount_percent: 20, applies_to_tier: "", applies_to_event_id: "",
    valid_until: "", max_uses: 1, is_active: true,
  });
  const [bulkForm, setBulkForm] = useState({
    prefix: "AA", start: 1, end: 50, pad: 2,
    discount_percent: 20, applies_to_tier: "Standard", applies_to_event_id: "",
    valid_until: "", max_uses: 1,
  });

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/vouchers`);
      setVouchers(res.data || []);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return vouchers.filter((v) => {
      if (filterStatus !== "all" && v.status !== filterStatus) return false;
      if (!q) return true;
      return v.code.toLowerCase().includes(q) || (v.applies_to_tier || "").toLowerCase().includes(q);
    });
  }, [vouchers, search, filterStatus]);

  const counts = useMemo(() => {
    const c = { all: vouchers.length, free: 0, reserved: 0, used: 0, expired: 0, disabled: 0 };
    vouchers.forEach((v) => { if (c[v.status] !== undefined) c[v.status] += 1; });
    return c;
  }, [vouchers]);

  const createSingle = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/vouchers`, singleForm);
      setShowSingle(false);
      setSingleForm({ code: "", discount_percent: 20, applies_to_tier: "", applies_to_event_id: "", valid_until: "", max_uses: 1, is_active: true });
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Gabim gjate ruajtjes.");
    } finally { setSaving(false); }
  };

  const createBulk = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.post(`${API}/vouchers/bulk`, bulkForm);
      setShowBulk(false);
      alert(`U krijuan ${res.data.created.length} kode. Kaluar (ekzistonin): ${res.data.skipped.length}`);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Gabim gjate gjenerimit.");
    } finally { setSaving(false); }
  };

  const toggleActive = async (v) => {
    try {
      await axios.patch(`${API}/vouchers/${v.id}`, { is_active: !v.is_active });
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "Gabim.");
    }
  };

  const deleteVoucher = async (v) => {
    if (!window.confirm(`Fshi kodin ${v.code}? Kjo bëhet vetëm nëse s'është përdorur.`)) return;
    try {
      await axios.delete(`${API}/vouchers/${v.id}`);
      await load();
    } catch (err) {
      alert(err.response?.data?.detail || "S'mund të fshihet.");
    }
  };

  const exportCsv = () => {
    window.open(`${API}/vouchers/export.csv`, "_blank");
  };

  return (
    <div className="p-6" data-testid="admin-vouchers">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold font-['Inter_Tight'] flex items-center gap-2"><Ticket size={22} /> Promo Code</h1>
          <p className="text-sm text-[#828D9E] mt-1">{vouchers.length} kode gjithsej</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCsv} className="rounded-lg px-4 py-2 text-sm font-semibold border border-slate-200 text-[#4A5568] hover:bg-slate-50 inline-flex items-center gap-2" data-testid="export-vouchers-btn">
            <Download size={14} /> Eksporto CSV
          </button>
          <button onClick={() => setShowBulk(true)} className="rounded-lg px-4 py-2 text-sm font-semibold text-white inline-flex items-center gap-2 bg-[#08707A] hover:bg-[#065A63]" data-testid="bulk-generate-btn">
            <Layers size={14} /> Gjenero ne grup
          </button>
          <button onClick={() => setShowSingle(true)} className="rounded-lg px-4 py-2 text-sm font-semibold text-white inline-flex items-center gap-2 bg-[#003F72] hover:bg-[#002F58]" data-testid="add-voucher-btn">
            <Plus size={14} /> Kod i ri
          </button>
        </div>
      </div>

      {/* Status pills */}
      <div className="flex flex-wrap gap-2 mb-4">
        {[
          { key: "all", label: "Te gjitha", bg: "#E2E8F0", fg: "#0F172A" },
          { key: "free", label: statusStyles.free.label, ...statusStyles.free },
          { key: "reserved", label: statusStyles.reserved.label, ...statusStyles.reserved },
          { key: "used", label: statusStyles.used.label, ...statusStyles.used },
          { key: "disabled", label: statusStyles.disabled.label, ...statusStyles.disabled },
          { key: "expired", label: statusStyles.expired.label, ...statusStyles.expired },
        ].map((f) => (
          <button
            key={f.key}
            onClick={() => setFilterStatus(f.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-bold transition-all ${filterStatus === f.key ? "ring-2 ring-offset-1 ring-[#003F72]" : ""}`}
            style={{ background: f.bg, color: f.fg }}
            data-testid={`filter-${f.key}`}
          >
            {f.label} <span className="opacity-70">({counts[f.key] ?? 0})</span>
          </button>
        ))}
      </div>

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
        <Input
          type="text"
          placeholder="Kerko kod ose bilete..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-10"
          data-testid="voucher-search"
        />
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#4A5568]">Kodi</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#4A5568]">Ulja</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#4A5568]">Bileta</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#4A5568]">Vlefshem deri</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#4A5568]">Perdorimet</th>
                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider text-[#4A5568]">Statusi</th>
                <th className="px-4 py-3 text-right text-xs font-bold uppercase tracking-wider text-[#4A5568]">Veprime</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr><td colSpan={7} className="text-center py-16 text-[#828D9E]">Duke ngarkuar...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-16 text-[#828D9E]">
                  <Ticket className="mx-auto mb-3 text-slate-300" size={40} />
                  <p>Nuk ka kode qe perputhen.</p>
                </td></tr>
              ) : filtered.map((v) => {
                const s = statusStyles[v.status] || statusStyles.free;
                const isExpanded = expandedId === v.id;
                return (
                  <React.Fragment key={v.id}>
                    <tr className="hover:bg-slate-50/50 transition-colors" data-testid={`voucher-row-${v.code}`}>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => setExpandedId(isExpanded ? null : v.id)}
                          className="font-mono font-bold text-[#003F72] hover:underline"
                          data-testid={`voucher-code-${v.code}`}
                        >
                          {v.code}
                        </button>
                      </td>
                      <td className="px-4 py-3 font-semibold text-[#0A0D10]">{v.discount_percent}%</td>
                      <td className="px-4 py-3 text-[#4A5568]">{v.applies_to_tier || <span className="italic text-slate-400">Te gjitha</span>}</td>
                      <td className="px-4 py-3 text-[#4A5568]">{v.valid_until ? formatDate(v.valid_until) : <span className="italic text-slate-400">Pa afat</span>}</td>
                      <td className="px-4 py-3 text-[#4A5568]">
                        <span className="font-mono">{v.used_count}/{v.max_uses}</span>
                        {v.reserved_count > 0 && <span className="ml-2 text-xs text-[#92400E]">(+{v.reserved_count} rez.)</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: s.bg, color: s.fg }}>
                          {s.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => toggleActive(v)}
                            title={v.is_active ? "Caktivizo" : "Aktivizo"}
                            className={`p-1.5 rounded-md transition-colors ${v.is_active ? "text-[#92400E] hover:bg-[#FEF3C7]" : "text-[#166534] hover:bg-[#DCFCE7]"}`}
                            data-testid={`toggle-${v.code}`}
                          >
                            <Power size={15} />
                          </button>
                          <button
                            onClick={() => deleteVoucher(v)}
                            title="Fshi"
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-md transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                            disabled={v.used_count > 0 || v.reserved_count > 0}
                            data-testid={`delete-${v.code}`}
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="bg-slate-50">
                        <td colSpan={7} className="px-6 py-4">
                          <p className="text-xs font-bold uppercase tracking-wider text-[#4A5568] mb-2">Historik i perdorimeve ({(v.usages || []).length})</p>
                          {(v.usages || []).length === 0 ? (
                            <p className="text-xs text-slate-400 italic">Asnje perdorim ende.</p>
                          ) : (
                            <ul className="space-y-1.5">
                              {(v.usages || []).map((u, idx) => (
                                <li key={idx} className="text-xs text-[#4A5568] bg-white rounded p-2 border border-slate-200">
                                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5">
                                    <span className="font-semibold text-[#0A0D10]">
                                      <i className="fas fa-user mr-1"></i>{u.customer_name || u.email || "—"}
                                    </span>
                                    {u.email && <span className="text-[#828D9E]"><i className="fas fa-envelope mr-1"></i>{u.email}</span>}
                                    <span><i className="fas fa-calendar mr-1"></i>{u.event_title || "—"}</span>
                                    <span>Ulje: <b>{u.discount_amount} €</b></span>
                                    <span>Paguar: <b>{u.final_amount} €</b></span>
                                    <span className="text-[#828D9E]">{formatDate(u.used_at)}</span>
                                    <a
                                      href={`/paneli/registrations?event=${encodeURIComponent(u.event_id || "")}&email=${encodeURIComponent(u.email || "")}`}
                                      className="ml-auto text-[#08707A] hover:underline font-semibold"
                                    >
                                      Shiko regjistrimin →
                                    </a>
                                  </div>
                                  <div className="text-[10px] text-slate-400 mt-1 font-mono">Order: {u.order_id}</div>
                                </li>
                              ))}
                            </ul>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Single create modal */}
      {showSingle && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowSingle(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="text-lg font-bold text-[#0A0D10]">Kod i ri</h2>
              <button onClick={() => setShowSingle(false)}><X size={20} /></button>
            </div>
            <form onSubmit={createSingle} className="p-5 space-y-3">
              <div>
                <Label>Kodi *</Label>
                <Input value={singleForm.code} onChange={(e) => setSingleForm({ ...singleForm, code: e.target.value.toUpperCase() })} placeholder="p.sh. AA01" required data-testid="single-code" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Ulja (%)</Label>
                  <Input type="number" min="0" max="100" step="0.5" value={singleForm.discount_percent} onChange={(e) => setSingleForm({ ...singleForm, discount_percent: parseFloat(e.target.value) })} required />
                </div>
                <div>
                  <Label>Max Perdorime</Label>
                  <Input type="number" min="1" value={singleForm.max_uses} onChange={(e) => setSingleForm({ ...singleForm, max_uses: parseInt(e.target.value) || 1 })} required />
                </div>
              </div>
              <div>
                <Label>Bileta (bosh = te gjitha)</Label>
                <Input value={singleForm.applies_to_tier} onChange={(e) => setSingleForm({ ...singleForm, applies_to_tier: e.target.value })} placeholder="p.sh. Standard" />
              </div>
              <div>
                <Label>Vlefshem deri (YYYY-MM-DD; bosh = pa afat)</Label>
                <Input type="date" value={singleForm.valid_until} onChange={(e) => setSingleForm({ ...singleForm, valid_until: e.target.value })} />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowSingle(false)} className="rounded-lg px-4 py-2 text-sm font-semibold border border-slate-200 text-[#4A5568] hover:bg-slate-50">Anulo</button>
                <button type="submit" disabled={saving} className="rounded-lg px-5 py-2 text-sm font-semibold text-white inline-flex items-center gap-2 bg-[#08707A] hover:bg-[#065A63] disabled:opacity-50" data-testid="single-save">
                  <Save size={14} /> {saving ? "Duke ruajtur..." : "Ruaj"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk generate modal */}
      {showBulk && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowBulk(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h2 className="text-lg font-bold text-[#0A0D10]">Gjenero kode ne grup</h2>
              <button onClick={() => setShowBulk(false)}><X size={20} /></button>
            </div>
            <form onSubmit={createBulk} className="p-5 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Prefiks</Label>
                  <Input value={bulkForm.prefix} onChange={(e) => setBulkForm({ ...bulkForm, prefix: e.target.value.toUpperCase() })} required />
                </div>
                <div>
                  <Label>Nga</Label>
                  <Input type="number" min="0" value={bulkForm.start} onChange={(e) => setBulkForm({ ...bulkForm, start: parseInt(e.target.value) || 0 })} required />
                </div>
                <div>
                  <Label>Deri</Label>
                  <Input type="number" min="0" value={bulkForm.end} onChange={(e) => setBulkForm({ ...bulkForm, end: parseInt(e.target.value) || 0 })} required />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Ulja (%)</Label>
                  <Input type="number" min="0" max="100" step="0.5" value={bulkForm.discount_percent} onChange={(e) => setBulkForm({ ...bulkForm, discount_percent: parseFloat(e.target.value) })} required />
                </div>
                <div>
                  <Label>Max Perdorime</Label>
                  <Input type="number" min="1" value={bulkForm.max_uses} onChange={(e) => setBulkForm({ ...bulkForm, max_uses: parseInt(e.target.value) || 1 })} required />
                </div>
              </div>
              <div>
                <Label>Bileta (bosh = te gjitha)</Label>
                <Input value={bulkForm.applies_to_tier} onChange={(e) => setBulkForm({ ...bulkForm, applies_to_tier: e.target.value })} placeholder="p.sh. Standard" />
              </div>
              <div>
                <Label>Vlefshem deri</Label>
                <Input type="date" value={bulkForm.valid_until} onChange={(e) => setBulkForm({ ...bulkForm, valid_until: e.target.value })} />
              </div>
              <p className="text-xs text-[#828D9E] italic">Kodet ekzistuese kalojne — proçesi eshte idempotent.</p>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setShowBulk(false)} className="rounded-lg px-4 py-2 text-sm font-semibold border border-slate-200 text-[#4A5568] hover:bg-slate-50">Anulo</button>
                <button type="submit" disabled={saving} className="rounded-lg px-5 py-2 text-sm font-semibold text-white inline-flex items-center gap-2 bg-[#08707A] hover:bg-[#065A63] disabled:opacity-50" data-testid="bulk-save">
                  <Layers size={14} /> {saving ? "Duke gjeneruar..." : `Gjenero ${Math.max(0, bulkForm.end - bulkForm.start + 1)} kode`}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVouchers;
